import React from "react";

import {
  LocalLogicMap,
  type Marker,
  type MarkerPoint,
} from "@local-logic/maps";
import { useTracking } from "~/tracking";
import { useNeighborhoodMap } from "~/sdks/NeighborhoodMap/context";

import {
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/NeighborhoodMap/types";

import { ClusterPopupContent } from "~/components/map/ClusterPopupContent";
import { POIPopupContent } from "~/components/map/POIPopupContent";

export function Popup() {
  const tracking = useTracking();
  const {
    activePoiCategory,
    activeMarker,
    activeMarkerDetails,
    handleMarkerClick,
    showSchoolRatings,
  } = useNeighborhoodMap();

  /**
   * Handles selecting a marker from within a cluster popup.
   *
   * @param marker
   * @returns
   */
  const handleClusterMarkerSelected = (marker: Marker) => {
    handleMarkerClick({
      id: marker.id,
      latitude: marker.latitude,
      longitude: marker.longitude,
      markers: [marker],
    } as MarkerPoint);

    if (!activePoiCategory) {
      return;
    }

    const eventAttributes: Record<string, boolean | string | number> = {
      category: activePoiCategory,
    };

    Object.entries(marker).forEach(([key, value]) => {
      if (
        typeof value === "boolean" ||
        typeof value === "string" ||
        typeof value === "number"
      ) {
        eventAttributes[key] = value;
      }
    });

    tracking.postEvent({
      eventType: TrackingPipelineEvents.POI_CLICKED,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.POI_CLICKED,
      eventAttributes,
    });
  };

  const handleLinkClick = (url: string) => {
    if (!activePoiCategory || !activeMarkerDetails) {
      return;
    }

    const eventAttributes: Record<string, boolean | string | number> = {
      category: activePoiCategory,
    };

    Object.entries(activeMarkerDetails).forEach(([key, value]) => {
      if (
        typeof value === "boolean" ||
        typeof value === "string" ||
        typeof value === "number"
      ) {
        eventAttributes[key] = value;
      }
    });
    eventAttributes.clickedUrl = url;

    tracking.postEvent({
      eventType: TrackingPipelineEvents.POI_LINK_CLICKED,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.POI_LINK_CLICKED,
      eventAttributes,
    });
  };

  return (
    <LocalLogicMap.Popup
      latitude={activeMarker?.latitude}
      longitude={activeMarker?.longitude}
      onClose={() => handleMarkerClick(undefined)}
    >
      <ClusterPopupContent
        activeMarker={activeMarker}
        onMarkerSelected={handleClusterMarkerSelected}
        onClose={handleMarkerClick}
      />
      <POIPopupContent
        marker={activeMarkerDetails}
        poiCategory={activePoiCategory}
        onLocationLinkClick={handleLinkClick}
        onSchoolLinkClick={handleLinkClick}
        onClose={handleMarkerClick}
        showSchoolRatings={showSchoolRatings}
      />
    </LocalLogicMap.Popup>
  );
}
